import React from 'react';
import { Link } from 'react-router';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { IconWrap } from '../components/IconWrap';
import useTheme from '../hooks/useTheme';
import { megaGreenData } from '../data/data';
import { CiBatteryFull, CiDroplet, CiShoppingCart } from 'react-icons/ci';
import { BiBuildings, BiCog, BiCar } from 'react-icons/bi';

export const Sectors: React.FC = () => {
    const { theme, breakpoint } = useTheme();
    const sectors = megaGreenData.sectors.sectors;

    return (
        <>
            <PageHero
                title="Sectors We Support"
                subtitle={megaGreenData.sectors.intro}
            />

            <Section>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '32px',
                    }}
                >
                    {sectors.map((sector, sectorIdx) => {
                        const sectorIcons = [
                            <BiCog />, // Industrial
                            <CiBatteryFull />, // Energy
                            <BiBuildings />, // Government
                            <CiDroplet />, // Commercial
                            <BiCar />, // Mining
                            <CiShoppingCart />, // Other
                        ];
                        const iconColors: Array<'primary' | 'secondary' | 'accent' | 'highlight' | 'feature'> = [
                            'primary',
                            'secondary',
                            'accent',
                            'highlight',
                            'feature',
                            'primary',
                        ];

                        return (
                            <Card key={sector.id} hoverable variant={sectorIdx % 2 === 0 ? 'primary' : 'secondary'}>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : '1fr 1fr',
                                        gap: '32px',
                                        marginBottom: '24px',
                                    }}
                                >
                                    <div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                marginBottom: '16px',
                                            }}
                                        >
                                            <IconWrap
                                                icon={sectorIcons[sectorIdx] || <BiCog />}
                                                size={40}
                                                color={iconColors[sectorIdx] || 'primary'}
                                                withBackground={true}
                                            />
                                            <h2
                                                style={{
                                                    fontSize: breakpoint.xs || breakpoint.sm ? '24px' : '28px',
                                                    fontWeight: 700,
                                                    color: theme.text.primary,
                                                }}
                                            >
                                                {sector.title}
                                            </h2>
                                        </div>
                                        <p
                                            style={{
                                                fontSize: '16px',
                                                color: theme.text.muted,
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {sector.short}
                                        </p>
                                    </div>
                                    <div>
                                        <img
                                            src={sector.src}
                                            alt={sector.title}
                                            style={{
                                                width: '100%',
                                                height: 200,
                                                objectFit: 'cover',
                                                borderRadius: theme.borderRadius.r1,
                                            }}
                                        />
                                    </div>
                                </div>

                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : '1fr 1fr',
                                        gap: '24px',
                                        marginBottom: '24px',
                                    }}
                                >
                                    <div>
                                        <h3
                                            style={{
                                                fontSize: '18px',
                                                fontWeight: 600,
                                                color: theme.text.primary,
                                                marginBottom: '12px',
                                            }}
                                        >
                                            Common challenges
                                        </h3>
                                        <ul
                                            style={{
                                                listStyle: 'none',
                                                padding: 0,
                                                margin: 0,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '8px',
                                            }}
                                        >
                                            {sector.commonChallenges.map((challenge, idx) => (
                                                <li
                                                    key={idx}
                                                    style={{
                                                        fontSize: '14px',
                                                        color: theme.text.muted,
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <span style={{ color: theme.palette.primary.main, marginTop: '4px', flexShrink: 0 }}>•</span>
                                                    {challenge}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div>
                                        <h3
                                            style={{
                                                fontSize: '18px',
                                                fontWeight: 600,
                                                color: theme.text.primary,
                                                marginBottom: '12px',
                                            }}
                                        >
                                            How Mega Green helps
                                        </h3>
                                        <ul
                                            style={{
                                                listStyle: 'none',
                                                padding: 0,
                                                margin: 0,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '8px',
                                            }}
                                        >
                                            {sector.howWeHelp.map((help, idx) => (
                                                <li
                                                    key={idx}
                                                    style={{
                                                        fontSize: '14px',
                                                        color: theme.text.muted,
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <span style={{ color: theme.palette.secondary.main, marginTop: '4px', flexShrink: 0 }}>•</span>
                                                    {help}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                <Link
                                    to={`/projects?sector=${sector.id}`}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Button variant="secondary">View related projects</Button>
                                </Link>
                            </Card>
                        );
                    })}
                </div>
            </Section>

            <Section
                style={{
                    backgroundColor: theme.background.panel,
                }}
            >
                <div
                    style={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        textAlign: 'center',
                    }}
                >
                    <h2
                        style={{
                            fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                            fontWeight: 700,
                            color: theme.text.primary,
                            marginBottom: '16px',
                        }}
                    >
                        Tell us what you're planning
                    </h2>
                    <p
                        style={{
                            fontSize: '16px',
                            color: theme.text.muted,
                            marginBottom: '24px',
                        }}
                    >
                        {megaGreenData.contact.pageIntro}
                    </p>
                    <Link to="/contact" style={{ textDecoration: 'none' }}>
                        <Button variant="primary" style={{ padding: '12px 32px' }}>
                            Request a Consultation
                        </Button>
                    </Link>
                </div>
            </Section>
        </>
    );
};

